import { defaultFilterName, SearchType } from 'app/models/GlobalSearch/GlobalSearch';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Collapse, Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { CustomFilter } from '../../../ComponentLibrary/Filter';
import { SearchPageIcons } from 'app/ComponentLibrary/Filter/SearchPageIcons';
import { gridStateActions } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.redux';
import {
  selectFilters,
  selectGridFiltersExpanded,
  selectGridState,
} from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.selectors';
import { userDevicePreferenceActions } from 'oidc/UserDevicePreference/userPreference.redux';
import { removeSessionValue, SessionKey, StorageType } from 'utils/customHooks/sessionStorage/sessionHelpers';
import { RootState } from 'types';
import _ from 'lodash';
import { isNullOrUndefined } from 'app/helpers/objectHelpers';
import { TasksFilter } from './FilterUtils';
import { taskSearchFilterName, defaultFilterValues } from './SearchUtils';

const useStyles = makeStyles()({
  fillterCollapse: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '3px',
  },
  collapse: {
    paddingTop: '4px',
  },
});

export const TaskSearchFilters = ({ defaultFilterValues: defaultValues }: { defaultFilterValues?: any }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { reset, setValue } = useFormContext();
  const gridStateFilters = useSelector(selectFilters);
  const gridState = useSelector(selectGridState);
  const gridSpecs = useRef(
    new TasksFilter(
      { statusOpts: [] },
      t,
      {},
      gridStateFilters,
    ),
  );
  const [fields, setFields] = useState(gridSpecs.current.getTaskFilterSpecs());

  const filterPreferences: any = useSelector((state: RootState) => {
    return state.userPreferenceData.userGridPreferences.find(item => item.id === 'GlobalSearchGrid-tasks');
  });
  
  const updateGridState = useCallback((filters: any) => {
    gridSpecs.current.gridStateFilters = filters;
    setFields(gridSpecs.current.getTaskFilterSpecs());
  }, []);

  useEffect(() => {
    updateGridState(gridStateFilters);
  }, [gridStateFilters, updateGridState]);

  const resetFilter = () => {
    const foundDefaultIndex = (filterPreferences?.value?.filterAttributes?.filterSet || [])?.find(
      item => item.name === defaultFilterName || item.isDefault,
    );

    if (filterPreferences?.value) {
      const filterPrefClone = _.cloneDeep(filterPreferences);

      if (!filterPrefClone.value) {
        filterPrefClone.value = {};
      }

      filterPrefClone.value.selectedFilter = foundDefaultIndex || null;
      dispatch(userDevicePreferenceActions.saveUserGridPreferenceAction(filterPrefClone));
    }
    removeSessionValue(SessionKey['GlobalSearchGrid-tasks'], StorageType.sessionStorage);

    setValue(taskSearchFilterName.status, defaultFilterValues?.[taskSearchFilterName.status]);
    setValue(taskSearchFilterName.from, defaultFilterValues?.[taskSearchFilterName.from]);
    setValue(taskSearchFilterName.recipients, defaultFilterValues?.[taskSearchFilterName.recipients]);
    setValue(taskSearchFilterName.sentDateRange, defaultFilterValues?.[taskSearchFilterName.sentDateRange]);
    setValue(taskSearchFilterName.followUp, defaultFilterValues?.[taskSearchFilterName.followUp]);
    setValue(taskSearchFilterName.followUpDateRange, defaultFilterValues?.[taskSearchFilterName.followUpDateRange]);
    setValue(taskSearchFilterName.priority, defaultFilterValues?.[taskSearchFilterName.priority]);
    setValue(taskSearchFilterName.optionApproval, defaultFilterValues?.[taskSearchFilterName.optionApproval]);
    setValue(taskSearchFilterName.categories, defaultFilterValues?.[taskSearchFilterName.categories]);
    setValue(taskSearchFilterName.facility, defaultFilterValues?.[taskSearchFilterName.facility]);
    setValue(taskSearchFilterName.oid, defaultFilterValues?.[taskSearchFilterName.oid]);
    setValue(taskSearchFilterName.pid, defaultFilterValues?.[taskSearchFilterName.pid]);
    setValue('flaggedFilter', []);
    setValue('coverageFilter', []);
    reset(!isNullOrUndefined(foundDefaultIndex) ? Object.fromEntries(foundDefaultIndex?.value) : defaultValues ?? defaultFilterValues);

    const targetFilterEntries = !isNullOrUndefined(foundDefaultIndex)
      ? foundDefaultIndex.value
      : Object.entries(defaultValues ?? defaultFilterValues);

    dispatch(
      gridStateActions.setFilterAttributes({
        ...gridState.filterAttributes,
        filterObject: targetFilterEntries,
        selectedChipFilters: {},
        associatedGridName: gridState.gridTag,
      }),
    );
    dispatch(gridStateActions.setSelectedChips({}));
  };

  const filtersExpanded = useSelector(selectGridFiltersExpanded);
  const handleChange = () => {
    dispatch(gridStateActions.setFiltersExpanded(!filtersExpanded));
  };

  return (
    <Grid container wrap="nowrap" display="flex">
      <Grid item flex="1 1 auto">
        <Collapse timeout={200} in={filtersExpanded} collapsedSize={45} className={classes.collapse}>
          <CustomFilter
            fields={fields}
            justifyContent="flex-start"
            version2
            resetBtn
            analytics={{
              caller: t('search.globalSearch.globalSearchFilter'),
              onApply: true,
              properties: {
                category: SearchType.tasks,
              },
            }}
          />
        </Collapse>
      </Grid>
      <Grid item flex="0 0 auto" className={classes.fillterCollapse}>
        <SearchPageIcons
          resetlbutton
          funnelButton
          checked={filtersExpanded}
          onResetForm={() => resetFilter()}
          onAdvancedFilterClick={() => handleChange()}
        />
      </Grid>
    </Grid>
  );
};
